import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Workflow, ArrowRight, Activity, Clock, CheckCircle2, AlertOctagon } from "lucide-react";

/**
 * OrchestrationsCard — V23.15.23 (Doctrine 49 P3 · transparence inter-agents).
 *
 * Affiche dans Settings/Agents :
 *  - Les règles d'orchestration déclarées (catalogue read-only)
 *  - Le compteur d'orchestrations en attente (queued)
 *  - Les 5 dernières orchestrations émises (transparence)
 */

const STATUS_META = {
  queued: { color: "#EF9F27", icon: Clock, label: "En attente" },
  consumed: { color: "#10B981", icon: CheckCircle2, label: "Traitée" },
  skipped: { color: "#71717A", icon: AlertOctagon, label: "Ignorée" },
};

export default function OrchestrationsCard() {
  const [rules, setRules] = useState([]);
  const [log, setLog] = useState([]);
  const [queuedCount, setQueuedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get("/workspace/agents/orchestration-rules"),
          api.get("/workspace/agents/orchestrations?limit=5"),
        ]);
        if (!mounted) return;
        setRules(r1.data?.rules || []);
        setLog(r2.data?.items || []);
        setQueuedCount(r2.data?.total_queued || 0);
      } catch {
        // Endpoint indispo : on cache silencieusement la carte
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) return null;
  if (rules.length === 0) return null;

  return (
    <div className="noam-card p-5 space-y-4" data-testid="orchestrations-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Workflow className="w-4 h-4 text-[#378ADD]" />
            <div className="noam-overline">Doctrine 49 · Orchestration inter-agents</div>
          </div>
          <h2 className="text-base font-medium text-zinc-100 mt-1">
            Vos agents coopèrent automatiquement quand le contexte le justifie
          </h2>
          <p className="text-xs text-zinc-300 mt-1.5 max-w-2xl leading-relaxed">
            Quand un agent détecte un signal significatif, il peut déclencher l&apos;action d&apos;un
            autre agent selon des règles déclaratives. Aucune permission n&apos;est jamais modifiée
            sans action explicite de votre part.
          </p>
        </div>
        {queuedCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#EF9F27]/10 border border-[#EF9F27]/30">
            <Activity className="w-3 h-3 text-[#EF9F27]" />
            <span className="text-[11px] text-[#EF9F27] font-medium" data-testid="orchestrations-queued-count">
              {queuedCount} en attente
            </span>
          </div>
        )}
      </div>

      {/* Catalogue des règles */}
      <div className="space-y-2" data-testid="orchestrations-rules-list">
        {rules.map((rule) => (
          <div
            key={rule.id}
            className="flex items-start gap-3 px-3 py-2.5 rounded-md bg-white/[0.02] border border-white/5"
            data-testid={`orchestration-rule-${rule.id}`}
          >
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[11px] font-medium text-[#378ADD] uppercase tracking-wider">
                {rule.source_agent}
              </span>
              <ArrowRight className="w-3 h-3 text-zinc-600" />
              <span className="text-[11px] font-medium text-emerald-400 uppercase tracking-wider">
                {rule.target_agent}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-zinc-300 leading-relaxed">{rule.description}</div>
              {rule.doctrine_ref && (
                <div className="text-[10px] text-zinc-500 mt-0.5">{rule.doctrine_ref}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Log des 5 dernières orchestrations */}
      {log.length > 0 && (
        <div className="pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            5 dernières exécutions
          </div>
          <ul className="space-y-1.5" data-testid="orchestrations-log-list">
            {log.map((entry) => {
              const meta = STATUS_META[entry.status] || STATUS_META.queued;
              const Icon = meta.icon;
              const ts = entry.created_at
                ? new Date(entry.created_at).toLocaleTimeString("fr-FR", {
                    hour: "2-digit", minute: "2-digit",
                  })
                : "—";
              return (
                <li
                  key={entry.id}
                  className="flex items-center gap-2 text-[11px]"
                  data-testid={`orchestration-log-row-${entry.status}`}
                >
                  <Icon className="w-3 h-3 flex-shrink-0" style={{ color: meta.color }} />
                  <span className="text-zinc-500">{ts}</span>
                  <span className="text-zinc-300">{entry.source_agent}</span>
                  <ArrowRight className="w-2.5 h-2.5 text-zinc-600" />
                  <span className="text-zinc-300">{entry.target_agent}</span>
                  <span className="text-zinc-500 ml-auto" style={{ color: meta.color }}>
                    {meta.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
